import { useState } from 'react';
import invoices, { InvoiceLedger } from '../../services/axleInvoices.service';

function errorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Invoice request failed. Check configuration and try again.';
}

export default function AxleInvoicesPanel() {
  const [data, setData] = useState<InvoiceLedger | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  async function action(kind: 'refresh' | 'scan' | 'send' | 'retry', billId?: string) {
    if (kind === 'send' && !window.confirm('Send queued Maintenance invoice PDFs to Axle’s LIVE production Inbox now?')) return;
    setBusy(true); setError(''); setNotice('');
    try {
      if (kind === 'scan' || kind === 'send') {
        const result = await (kind === 'scan' ? invoices.scan() : invoices.run());
        setNotice(kind === 'scan'
          ? `Scanned ${result.records} bills. Nothing sent to Axle.${result.more_pages ? ' More pages available; scan again to continue.' : ''}`
          : `${result.updated} bills stored, ${result.errors} needing attention. Status: ${result.status}.`);
      }
      if (kind === 'retry' && billId) {
        const result = await invoices.retry(billId);
        setNotice(result.status === 'queued' ? 'Invoice queued for the next enabled delivery run.' : 'Another invoice operation is running. Try again shortly.');
      }
      setData(await invoices.list());
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  const buttonClass = 'px-3 py-2 rounded border border-default text-sm disabled:opacity-50';
  return <section className="my-6 rounded-lg border border-default bg-panel p-4" aria-label="Ramp invoice delivery">
    <h2 className="text-lg font-semibold text-heading">Ramp invoices</h2>
    <p className="text-sm text-muted mb-3">Maintenance invoice PDFs sent to Axle. Axle delivery is production-only, including from this test environment. Stored means received; Axle performs matching afterward.</p>
    <div className="flex flex-wrap gap-2 mb-3">
      <button className={buttonClass} disabled={busy} onClick={() => action('refresh')}>Load invoice queue</button>
      <button className={buttonClass} disabled={busy || !data} onClick={() => action('scan')}>Scan Ramp — no sending</button>
      <button className={buttonClass} disabled={busy || !data?.enabled} onClick={() => action('send')}>Send queued invoices to live Axle</button>
    </div>
    {data && <p className="text-sm text-muted mb-3">Sending: {data.enabled ? 'Enabled' : 'Disabled'} · Starting date: {data.start_date || 'Not configured'}</p>}
    {notice && <p role="status" className="text-sm mb-3">{notice}</p>}
    {error && <p role="alert" className="text-sm text-red-600 mb-3">{error}</p>}
    {data && <div className="overflow-auto"><table className="w-full text-sm text-left">
      <thead><tr>{['Invoice', 'State', 'Stored files', 'Attempts', 'Last error', ''].map((label) => <th key={label} className="p-2">{label}</th>)}</tr></thead>
      <tbody>{data.rows.map(row => <tr key={row.bill_id} className="border-t border-default">
        <td className="p-2">{row.invoice_number || row.bill_id}</td><td className="p-2">{row.state}</td>
        <td className="p-2">{row.stored_files}</td><td className="p-2">{row.attempts}</td><td className="p-2">{row.last_error}</td>
        <td className="p-2">{(row.state === 'blocked' || row.state === 'retry') && <button className={buttonClass} disabled={busy} onClick={() => action('retry', row.bill_id)}>Queue retry</button>}</td>
      </tr>)}</tbody>
    </table>{data.rows.length === 0 && <p className="p-2 text-muted">No invoices queued. Configure the Maintenance filter and start date, then scan Ramp.</p>}</div>}
  </section>;
}
